"use client";
import { useState } from 'react';
import { useSpring, animated, config, useSpringRef, useChain } from '@react-spring/web';
import { GradientText } from '../ui/gradient-text';
import { ArrowRight, Calendar, MapPin, Sparkles, ArrowDownToDot } from 'lucide-react';
import RegistrationModal from '../ui/RegistrationModal';
import { useSeatsData } from '@/hooks/useSeatsData';

export default function HeroSection() {
  const { showUrgency, remainingSeats, totalSeats } = useSeatsData();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Animation springs
  const badgeRef = useSpringRef();
  const badgeAnimation = useSpring({
    ref: badgeRef,
    from: { opacity: 0, transform: 'translateY(-20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { ...config.default, duration: 600 }
  });

  const headlineRef = useSpringRef();
  const headlineAnimation = useSpring({
    ref: headlineRef,
    from: { opacity: 0, transform: 'translateY(30px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { ...config.default, duration: 800 }
  });

  const subheadlineRef = useSpringRef();
  const subheadlineAnimation = useSpring({
    ref: subheadlineRef,
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { ...config.default, duration: 800 }
  });

  const eventCardRef = useSpringRef();
  const eventCardAnimation = useSpring({
    ref: eventCardRef,
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { ...config.default, duration: 800 }
  });

  const urgencyAnimation = useSpring({
    from: { opacity: 0, scale: 0.95 },
    to: { opacity: showUrgency ? 1 : 0, scale: showUrgency ? 1 : 0.95 },
    config: { ...config.default, duration: 600 }
  });

  const pulseAnimation = useSpring({
    from: { scale: 1, opacity: 1 },
    to: async (next) => {
      while (true) {
        await next({ scale: 1.2, opacity: 0.8 });
        await next({ scale: 1, opacity: 1 });
      }
    },
    config: { duration: 1500 }
  });

  const ctaRef = useSpringRef();
  const ctaAnimation = useSpring({
    ref: ctaRef,
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { ...config.default, duration: 800 }
  });

  // Stamp animation - delayed by 1 second
  const stampAnimation = useSpring({
    from: { opacity: 0, transform: 'scale(0.8)' },
    to: { opacity: remainingSeats === 0 ? 1 : 0, transform: remainingSeats === 0 ? 'scale(1)' : 'scale(0.8)' },
    config: { ...config.default, duration: 600 },
    delay: 1000 // 1 second delay
  });

  // Chain animations
  useChain([badgeRef, headlineRef, subheadlineRef, eventCardRef, ctaRef], [0, 0.1, 0.3, 0.4, 0.6]);

  return (
    <section id="hero" className="relative min-h-screen w-full flex items-start overflow-hidden bg-white">
      {/* Sold Out Stamp Overlay */}
      {remainingSeats === 0 && (
        <animated.div 
          className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
          style={stampAnimation}
        >
          <div className="transform rotate-[-30deg] translate-y-[-100px] sm:translate-y-[-120px] md:translate-y-[-150px]">
            <div className="relative bg-white/30 backdrop-blur-sm rounded-2xl border-8 border-red-600 px-8 sm:px-12 md:px-16 py-4 sm:py-6 md:py-8">
              <p className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-heading font-bold text-red-600 uppercase tracking-wider opacity-90">
                SOLD OUT
              </p>
            </div>
          </div>
        </animated.div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 xs:px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 md:pt-32 lg:pt-36 pb-8 sm:pb-12 md:pb-16 w-full">
        <div className="text-center">
            {/* Premium Badge */}
            <animated.div
              className="inline-flex items-center gap-2 mb-4 sm:mb-8 md:mb-10"
              style={badgeAnimation}
            >
              <div className="flex items-center gap-2 bg-black/5 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-black/10">
                <Sparkles className="w-3 sm:w-4 h-3 sm:h-4 text-[#97d700]" />
                <span className="text-xs sm:text-sm font-medium uppercase tracking-wider text-black/80">
                  Avalada por ICF, ZINQUO y MDS GROUP
                </span>
              </div>
            </animated.div>

            {/* Main Headline with Gradient */}
            <animated.div
              className="mb-8 sm:mb-8 max-w-[95%] sm:max-w-none mx-auto"
              style={headlineAnimation}
            >
              <h1 className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl font-heading uppercase tracking-tight leading-[0.9] sm:leading-[0.85]">
                <span className="block py-6 text-black text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl">Certificación Internacional
</span>       <GradientText
                  as="span"
                  className="block text-6xl xs:text-6xl sm:text-7xl md:text-8xl lg:text-8xl xl:text-9xl font-bold mb-3 sm:mb-3 md:mb-3"
                >
                  Coaching X Valores
                </GradientText>


              </h1>
            </animated.div>

            {/* Subheadline */}
            <animated.p
              className="text-lg sm:text-xl md:text-2xl lg:text-2xl text-black/70 mb-8 mt-10 sm:mt-6 md:mt-8 sm:mb-8 lg:mb-10 max-w-3xl mx-auto leading-relaxed"
              style={subheadlineAnimation}
            >
              <span className="block">Eleva tu <span className="font-bold text-black">práctica profesional</span> mientras</span>
              <span className="block">vives tu <span className="font-bold text-black">propia transformación</span></span>
            </animated.p>

            {/* Event Details Card */}
            <animated.div
              className="max-w-2xl mx-auto mb-6 sm:mb-8"
              style={eventCardAnimation}
            >
              <div className="bg-white/50 backdrop-blur-md rounded-2xl p-4 sm:p-5 md:p-6 shadow-xl border border-white/60">
                <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-11 md:w-12 sm:h-11 md:h-12 bg-[#97d700]/10 rounded-xl flex items-center justify-center">
                      <Calendar className="w-5 h-5 sm:w-5 md:w-6 sm:h-5 md:h-6 text-[#97d700]" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs sm:text-sm md:text-sm text-black/60 uppercase tracking-wider">Fechas</p>
                      <p className="text-sm sm:text-base md:text-lg font-bold text-black">18-20 Julio</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-11 md:w-12 sm:h-11 md:h-12 bg-[#97d700]/10 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 sm:w-5 md:w-6 sm:h-5 md:h-6 text-[#97d700]" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs sm:text-sm md:text-sm text-black/60 uppercase tracking-wider">Ubicación</p>
                      <p className="text-sm sm:text-base md:text-lg font-bold text-black">Alicante</p>
                    </div>
                  </div>
                </div>

                {/* Urgency indicator */}
                {showUrgency && (
                  <animated.div
                    className="relative overflow-hidden bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white via-red-60 to-red-100 rounded-xl px-4 py-2 mb-4 shadow-xl border border-red-300/10"
                    style={urgencyAnimation}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <animated.span
                        className="relative w-4 h-4 rounded-full shadow-lg bg-red-600"
                        style={pulseAnimation}
                      >
                        {/* Removed animate-ping to reduce flickering on mobile */}
                      </animated.span>
                      <span className="text-sm sm:text-base md:text-lg font-bold uppercase tracking-wide text-black">
                        {remainingSeats === 0 ? 'TODAS LAS PLAZAS VENDIDAS' : `Solo ${remainingSeats} plaza${remainingSeats !== 1 ? 's' : ''} restante${remainingSeats !== 1 ? 's' : ''}`}
                      </span>
                    </div>
                  </animated.div>
                )}

                {/* Value Props - Hidden on mobile */}
                <div className="hidden sm:grid grid-cols-3 gap-2 text-center">
                  <div className="bg-black/5 rounded-lg p-2 sm:p-3">
                    <p className="text-lg sm:text-xl font-bold text-[#97d700]">2 en 1</p>
                    <p className="text-xs sm:text-sm text-black/70">Presencial + Online</p>
                  </div>
                  <div className="bg-black/5 rounded-lg p-2 sm:p-3">
                    <p className="text-lg sm:text-xl font-bold text-[#97d700]">{totalSeats}</p>
                    <p className="text-xs sm:text-sm text-black/70">Plazas limitadas</p>
                  </div>
                  <div className="bg-black/5 rounded-lg p-2 sm:p-3">
                    <p className="text-lg sm:text-xl font-bold text-[#97d700]">ICF</p>
                    <p className="text-xs sm:text-sm text-black/70">Certificación Homologada</p>
                  </div>
                </div>
              </div>
            </animated.div>

            {/* CTA Section */}
            <animated.div
              style={ctaAnimation}
              className="space-y-4"
            >
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="group relative inline-flex items-center gap-2 px-6 py-3 bg-[#97d700] text-black font-heading uppercase tracking-wider font-bold text-base rounded-full shadow-xl hover:shadow-[#97d700]/30 transform hover:scale-[1.02] transition-all duration-300"
                  aria-label="Solicitar admisión para la certificación"
                >
                  <span>Solicitar Admisión</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href="#benefits"
                  onClick={(e) => handleSmoothScroll(e, '#benefits')}
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm text-black font-medium text-base rounded-full border border-black/10 hover:bg-white hover:border-black/20 hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300"
                  aria-label="Descubrir más sobre los beneficios de la certificación"
                >
                  <span>Descubre más</span>
                  <ArrowDownToDot className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
                </a>
              </div>

            </animated.div>
          </div>
        </div>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      </section>
  );
}